import {
  ArrowRight,
  Check,
  CheckCheck,
  ChevronRight,
  Circle,
  Lock,
  RotateCcw,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bubble } from "@/components/diagrams/Bubble";
import { SmokeCanvas } from "@/components/fx/SmokeCanvas";
import { resolveWorkshopDiagram } from "@/features/workshop/resolve-workshop-diagram";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { useProgress, activityKey } from "@/stores/useProgress";
import { usePowerUser } from "@/stores/usePowerUser";
import { cascadePhases, setWorkshopComplete } from "@/lib/workshop-completion";
import {
  WORKSHOP_PANEL_DATA,
  normalizeActivityStatuses,
  type AccentKey,
  type ActivityStatus,
  type WorkshopActivity,
  type WorkshopPanelData,
} from "./workshop-panel-data";

/**
 * Generic workshop bubble panel. Reads the layout, accent, bubbles and
 * connectors from `WORKSHOP_PANEL_DATA[workshopId]` and renders:
 *
 *   - SmokeCanvas backdrop (tinted to the workshop accent)
 *   - Optional left-column wordmark (WS4 only in the vanilla)
 *   - One SVG with accent-themed gradient + glow filter defs + the
 *     bubble triangle / 2×2 grid / 5-cross / single-row layout
 *   - Right-hand brief + activities column ported from the per-`MC_WSn`
 *     blocks in `_legacy/mc.html` (e.g. WS4 lines 354-436). Activity
 *     cards mirror the legacy treatment:
 *       - `done`     green pulse dot + green-tinted card (no chevron)
 *       - `active`   accent-tinted card with stronger border + glow
 *                    + an `ACTIVE` eyebrow on the right
 *       - `locked`   muted card with a hollow dot + lock icon
 *
 * Replaces the per-panel `Ws*Panel` + `Ws*Defs` files that would
 * otherwise be needed for each of the seven non-WS4 workshops.
 *
 * Gradient + glow IDs are namespaced per workshop (`grad-WS1`,
 * `glow-WS3`, …) so multiple panels can coexist if a future layout
 * ever mounts them side-by-side.
 */

const ACCENT_HSL: Record<AccentKey, string> = {
  "true-blue": "--accent-true-blue-hsl",
  sky: "--accent-sky-hsl",
  gold: "--accent-gold-hsl",
  melon: "--accent-melon-hsl",
};

const ACCENT_VAR: Record<AccentKey, string> = {
  "true-blue": "var(--accent-true-blue)",
  sky: "var(--accent-sky)",
  gold: "var(--accent-gold)",
  melon: "var(--accent-melon)",
};

const ACCENT_RGB: Record<AccentKey, string> = {
  "true-blue": "var(--accent-true-blue-rgb)",
  sky: "var(--accent-sky-rgb)",
  gold: "var(--accent-gold-rgb)",
  melon: "var(--accent-melon-rgb)",
};

const ACCENT_CSS_VAR: Record<AccentKey, string> = {
  "true-blue": "--accent-true-blue",
  sky: "--accent-sky",
  gold: "--accent-gold",
  melon: "--accent-melon",
};

function PanelDefs({ id, accent }: { id: string; accent: AccentKey }) {
  const hslVar = ACCENT_HSL[accent];
  return (
    <defs>
      <radialGradient id={`grad-${id}`} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={`hsl(var(${hslVar}) / 0.13)`} />
        <stop offset="100%" stopColor={`hsl(var(${hslVar}) / 0.02)`} />
      </radialGradient>
      <filter id={`glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

function Wordmark({
  wordmark,
  accent,
  className = "flex",
}: {
  wordmark: NonNullable<WorkshopPanelData["wordmark"]>;
  accent: AccentKey;
  /** Lets the caller drop the wordmark where it no longer fits. */
  className?: string;
}) {
  const accentVar = ACCENT_VAR[accent];
  const hslVar = ACCENT_HSL[accent];
  return (
    <div
      className={`text-foreground pointer-events-none flex-col items-center justify-center gap-1 px-3 ${className}`}
    >
      <span
        className="font-sans text-sm font-bold tracking-[0.25em] uppercase"
        style={{
          color: accentVar,
          // Text-shadow tinted to the workshop's accent — every panel
          // glows in its own palette (true-blue for WS1/2, sky for
          // WS3/4, gold for WS5/6/7, melon for WS8) instead of every
          // panel borrowing WS4's sky.
          textShadow: `0 0 6px hsl(var(${hslVar}) / 0.9), 0 0 16px hsl(var(${hslVar}) / 0.6), 0 0 32px hsl(var(${hslVar}) / 0.3)`,
        }}
      >
        {wordmark.eyebrow}
      </span>
      {wordmark.lines.map((line) => (
        <span
          key={line}
          className="font-sans text-base font-bold whitespace-nowrap uppercase"
          style={{
            letterSpacing: "0.3em",
            textShadow:
              "0 0 6px rgba(255,248,236,0.95), 0 0 16px rgba(255,248,236,0.7), 0 0 40px rgba(255,248,236,0.4)",
          }}
        >
          {line}
        </span>
      ))}
      <div className="text-muted-foreground mt-2 flex flex-col items-center gap-px text-fs-label tracking-wider">
        {wordmark.subtitle.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </div>
    </div>
  );
}

/**
 * One activity card. Mirrors the three legacy treatments verbatim:
 *   - `done`     `background: rgba(var(--accent-green-rgb), 0.05)`
 *                with a `0.2` border + filled green pulse dot.
 *   - `active`   `background: rgba(var(<accent>-rgb), 0.07)` +
 *                `0.35` border + `0 0 12px / 0.08` glow + filled
 *                accent pulse dot + uppercase `ACTIVE` eyebrow.
 *   - `locked`   `background: var(--bg-card)` + `0.15` accent border
 *                + hollow muted dot + lock icon.
 *
 * Click expands `details` when present (WS4 only ships meaningful
 * details — other workshops collapse the card to its label).
 */
function ActivityCard({
  activity,
  accent,
  status,
  interactive,
  onToggle,
}: {
  activity: WorkshopActivity;
  accent: AccentKey;
  /** Effective status (override ?? static default), resolved by the
   *  parent so the whole list shares one derived "active" frontier. */
  status: ActivityStatus;
  /** RT-GATING — when false the status glyph is shown read-only (no
   *  toggle). True only in power-user (admin) mode. */
  interactive: boolean;
  /** Flip this activity's completion. "active" is derived, never set
   *  directly — the parent re-normalises the frontier on each toggle. */
  onToggle: () => void;
}) {
  const accentVar = ACCENT_VAR[accent];
  const accentRgb = ACCENT_RGB[accent];
  const hasDetails = (activity.details?.length ?? 0) > 0;
  const [open, setOpen] = useState(status === "active");

  const toggleBtn = (
    <StatusToggleButton
      status={status}
      interactive={interactive}
      onToggle={onToggle}
      label={activity.title}
    />
  );

  const baseClass =
    "flex items-start gap-2.5 rounded-lg px-3 py-2 transition-all";
  const cursor = hasDetails ? "cursor-pointer select-none" : "";

  if (status === "active") {
    return (
      <div
        className={`${baseClass} ${cursor}`}
        style={{
          background: `rgba(${accentRgb}, 0.07)`,
          border: `1px solid rgba(${accentRgb}, 0.35)`,
          boxShadow: `0 0 12px rgba(${accentRgb}, 0.08)`,
        }}
        onClick={hasDetails ? () => setOpen((v) => !v) : undefined}
      >
        <span
          aria-hidden
          className="mt-1 inline-block h-2 w-2 flex-shrink-0 animate-pulse rounded-full"
          style={{
            background: accentVar,
            boxShadow: `0 0 8px rgba(${accentRgb}, 0.6)`,
          }}
        />
        <div className="flex flex-1 flex-col gap-0.5">
          <div className="flex items-center justify-between">
            <span
              className="font-sans font-semibold"
              style={{
                color: accentVar,
                fontSize: "var(--fs-base)",
              }}
            >
              {activity.title}
            </span>
            <span
              className="font-sans font-bold uppercase"
              style={{
                color: accentVar,
                fontSize: "var(--fs-label)",
                letterSpacing: "1.5px",
              }}
            >
              Active
            </span>
          </div>
          {hasDetails && open && (
            <div className="flex flex-col gap-0.5">
              {activity.details!.map((line) => (
                <span
                  key={line}
                  className="font-sans"
                  style={{
                    color: accentVar,
                    fontSize: "var(--fs-base)",
                    letterSpacing: "0.3px",
                    opacity: 0.8,
                  }}
                >
                  {line}
                </span>
              ))}
            </div>
          )}
        </div>
        {toggleBtn}
      </div>
    );
  }

  if (status === "done") {
    return (
      <div
        className={`${baseClass} ${cursor}`}
        style={{
          background: "rgba(var(--accent-green-rgb), 0.05)",
          border: "1px solid rgba(var(--accent-green-rgb), 0.2)",
        }}
        onClick={hasDetails ? () => setOpen((v) => !v) : undefined}
      >
        <span
          aria-hidden
          className="mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full"
          style={{
            background: "var(--accent-green)",
            boxShadow: "var(--glow-green)",
          }}
        />
        <div className="flex flex-1 flex-col gap-0.5">
          <div className="flex items-center justify-between">
            <span
              className="font-sans font-medium"
              style={{
                color: "var(--text-primary)",
                fontSize: "var(--fs-base)",
              }}
            >
              {activity.title}
            </span>
            {hasDetails ? (
              <ChevronRight
                className="h-3.5 w-3.5 transition-transform"
                style={{
                  transform: open ? "rotate(90deg)" : "rotate(0deg)",
                  color: "var(--text-muted)",
                }}
                aria-hidden
              />
            ) : (
              <Check
                className="h-3.5 w-3.5"
                style={{ color: "var(--accent-green)" }}
                aria-hidden
              />
            )}
          </div>
          {hasDetails && open && (
            <div className="flex flex-col gap-0.5">
              {activity.details!.map((line) => (
                <div
                  key={line}
                  className="flex items-center gap-1.5"
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "var(--fs-base)",
                  }}
                >
                  <span>{line}</span>
                  <Check
                    className="h-3 w-3 flex-shrink-0"
                    style={{ color: "var(--accent-green)" }}
                    aria-hidden
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {toggleBtn}
      </div>
    );
  }

  // locked
  return (
    <div
      className={`${baseClass} ${cursor}`}
      style={{
        background: "var(--bg-card)",
        border: `1px solid rgba(${accentRgb}, 0.15)`,
      }}
      onClick={hasDetails ? () => setOpen((v) => !v) : undefined}
    >
      <span
        aria-hidden
        className="mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full"
        style={{
          border: "1.5px solid var(--text-muted)",
        }}
      />
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center justify-between">
          <span
            className="font-sans font-medium"
            style={{
              color: "var(--text-muted)",
              fontSize: "var(--fs-base)",
            }}
          >
            {activity.title}
          </span>
          {hasDetails ? (
            <ChevronRight
              className="h-3.5 w-3.5 transition-transform"
              style={{
                transform: open ? "rotate(90deg)" : "rotate(0deg)",
                color: "var(--text-muted)",
              }}
              aria-hidden
            />
          ) : (
            <Lock
              className="h-3 w-3"
              style={{ color: "var(--text-muted)" }}
              aria-hidden
            />
          )}
        </div>
        {hasDetails && open && (
          <div className="flex flex-col gap-0.5">
            {activity.details!.map((line) => (
              <span
                key={line}
                className="font-sans"
                style={{
                  color: "var(--text-muted)",
                  fontSize: "var(--fs-base)",
                  letterSpacing: "0.3px",
                  opacity: 0.8,
                }}
              >
                {line}
              </span>
            ))}
          </div>
        )}
      </div>
      {toggleBtn}
    </div>
  );
}

/**
 * Compact per-activity completion toggle. A two-state checkbox — done /
 * not-done — never a manual "active" setter: "active" is the derived
 * frontier (first incomplete activity), re-normalised by the parent on
 * each toggle. Completing advances the frontier to the next activity;
 * un-completing falls it back to the one just reopened. `stopPropagation`
 * so it doesn't also toggle the card's details expander.
 */
function StatusToggleButton({
  status,
  interactive,
  onToggle,
  label,
}: {
  status: ActivityStatus;
  /** RT-GATING — false renders a read-only status glyph (admin mode off). */
  interactive: boolean;
  onToggle: () => void;
  label: string;
}) {
  const done = status === "done";
  const Icon = done ? Check : Circle;
  const visual = {
    flexShrink: 0,
    marginTop: 2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: 6,
    border: done
      ? "1px solid rgba(var(--accent-green-rgb), 0.4)"
      : "1px solid hsl(var(--border))",
    background: done ? "rgba(var(--accent-green-rgb), 0.08)" : "transparent",
    color: done ? "var(--accent-green)" : "var(--text-muted)",
  } as const;

  // Read-only: show the status glyph but offer no toggle (admin mode off).
  if (!interactive) {
    return (
      <span
        role="img"
        aria-label={done ? `${label} complete` : `${label} not complete`}
        title={done ? "Complete" : "Not complete"}
        style={{ ...visual, cursor: "default" }}
      >
        <Icon style={{ width: 12, height: 12 }} strokeWidth={2} aria-hidden />
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-label={done ? `Mark ${label} incomplete` : `Mark ${label} complete`}
      aria-pressed={done}
      title={done ? "Complete — click to reopen" : "Click to mark complete"}
      style={{ ...visual, cursor: "pointer" }}
    >
      <Icon style={{ width: 12, height: 12 }} strokeWidth={2} aria-hidden />
    </button>
  );
}

function ActivitiesColumn({
  data,
  accent,
  onEnterWorkshop,
}: {
  data: WorkshopPanelData;
  accent: AccentKey;
  onEnterWorkshop: () => void;
}) {
  const accentVar = ACCENT_VAR[accent];
  const accentRgb = ACCENT_RGB[accent];
  const activityStatusMap = useProgress((s) => s.activityStatus);
  const setActivityStatuses = useProgress((s) => s.setActivityStatuses);
  // RT-GATING — activity toggles + the workshop sign-off mutate program
  // state, so they require power-user (admin) mode. Read-only viewers see
  // the activities + their status but can't change them.
  const isPowerUnlocked = usePowerUser((s) => s.isUnlocked);

  const orderedIds = data.activities.map((a) => a.id);
  const effectiveStatus = (a: WorkshopActivity): ActivityStatus =>
    activityStatusMap[activityKey(data.workshopId, a.id)] ?? a.status;
  const allDone =
    data.activities.length > 0 &&
    data.activities.every((a) => effectiveStatus(a) === "done");

  // Flip one activity's completion, then re-derive the whole list from the
  // resulting done-set so there's always exactly one "active" frontier
  // (the first incomplete activity). Mirrors the Run Sequence per-phase
  // toggle: completing advances the frontier to the next activity,
  // un-completing falls it back to the one just reopened.
  function toggleActivity(activity: WorkshopActivity): void {
    const doneIds = new Set(
      data.activities
        .filter((a) => effectiveStatus(a) === "done")
        .map((a) => a.id),
    );
    if (doneIds.has(activity.id)) doneIds.delete(activity.id);
    else doneIds.add(activity.id);
    const normalized = normalizeActivityStatuses(orderedIds, doneIds);
    const updates: Record<string, ActivityStatus> = {};
    for (const id of orderedIds) {
      updates[activityKey(data.workshopId, id)] = normalized[id]!;
    }
    setActivityStatuses(updates);
    // Cross-surface invariant: when toggling activities crosses the "whole
    // workshop done" boundary, cascade the Run Sequence phases + the
    // navigator badge so the stepper and badge can't disagree with these
    // cards. Phases are a separate decomposition with their own ids, so they
    // can only be coupled at this boundary; partial activity progress stays
    // independent of partial phase progress. `allDone` is the pre-toggle
    // state (closure). cascadePhases (not setWorkshopComplete) so reopening
    // never clobbers the activity frontier we just wrote.
    const isAllDone = orderedIds.every((id) => normalized[id] === "done");
    if (isAllDone !== allDone) cascadePhases(data.workshopId, isAllDone);
  }

  return (
    <aside className="flex min-h-0 flex-col gap-2.5 overflow-hidden">
      {/* Brief eyebrow + headline */}
      <div className="flex-shrink-0">
        <div
          className="font-sans uppercase"
          style={{
            color: "var(--text-muted)",
            fontSize: "var(--fs-base)",
            letterSpacing: "2px",
            marginBottom: "4px",
          }}
        >
          Brief
        </div>
        <div
          className="font-sans font-bold uppercase"
          style={{
            color: accentVar,
            fontSize: "var(--fs-heading)",
            letterSpacing: "1px",
            lineHeight: 1.3,
          }}
        >
          {data.brief}
        </div>
      </div>

      {/* Activity list — scrolls when overflow */}
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pr-1">
        {data.activities.length === 0 ? (
          <span
            className="font-sans italic"
            style={{
              color: "var(--text-muted)",
              fontSize: "var(--fs-base)",
            }}
          >
            Activities pending
          </span>
        ) : (
          data.activities.map((activity) => (
            <ActivityCard
              key={activity.id}
              activity={activity}
              accent={accent}
              status={effectiveStatus(activity)}
              interactive={isPowerUnlocked}
              onToggle={() => toggleActivity(activity)}
            />
          ))
        )}
      </div>

      {/* Bottom action stack. The workshop-level "mark complete" sits
          directly above the primary "Enter Workshop" CTA so a facilitator
          can sign off a whole workshop from the panel they're reviewing.
          It cascades to the Run Sequence phases + the navigator badge via
          setWorkshopComplete, so every surface stays in sync (and flips to
          "Reopen Workshop" once everything is done). */}
      <div className="flex flex-shrink-0 flex-col gap-2">
        {/* RT-GATING — workshop sign-off requires power-user (admin) mode. */}
        {isPowerUnlocked && (
          <button
            type="button"
            onClick={() => setWorkshopComplete(data.workshopId, !allDone)}
            aria-pressed={allDone}
            className="flex items-center justify-center gap-2 rounded-lg px-4 py-2 font-sans font-bold uppercase transition-all"
            style={
              allDone
                ? {
                    background: "transparent",
                    border: "1px solid hsl(var(--border))",
                    color: "var(--text-muted)",
                    fontSize: "var(--fs-label)",
                    letterSpacing: "1.5px",
                  }
                : {
                    background: `rgba(${accentRgb}, 0.04)`,
                    border: `1px solid rgba(${accentRgb}, 0.3)`,
                    color: accentVar,
                    fontSize: "var(--fs-label)",
                    letterSpacing: "1.5px",
                  }
            }
          >
            {allDone ? (
              <>
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                Reopen Workshop
              </>
            ) : (
              <>
                <CheckCheck className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                Mark Workshop Complete
              </>
            )}
          </button>
        )}

        <EnterWorkshopButton accent={accent} onEnterWorkshop={onEnterWorkshop} />
      </div>
    </aside>
  );
}

/**
 * Enter-workshop CTA — matches legacy WS4 panel button at the bottom of
 * the activities column (mc.html line 431). Pulled out of
 * `ActivitiesColumn` so the phone diagram tab can render its own copy
 * (see `WorkshopPanel` below) — on phone the CTA otherwise only existed
 * on the Brief tab, so entering a workshop straight from its diagram
 * meant switching tabs first for no reason.
 */
function EnterWorkshopButton({
  accent,
  onEnterWorkshop,
}: {
  accent: AccentKey;
  onEnterWorkshop: () => void;
}) {
  const accentRgb = ACCENT_RGB[accent];
  return (
    <button
      type="button"
      onClick={onEnterWorkshop}
      className="cursor-pointer rounded-lg px-4 py-2.5 font-sans font-bold uppercase transition-all"
      style={{
        background: "var(--bg-panel-alt)",
        border: `1px solid rgba(${accentRgb}, 0.45)`,
        color: "var(--text-primary)",
        fontSize: "var(--fs-sub)",
        letterSpacing: "3px",
        boxShadow: `0 0 18px rgba(${accentRgb}, 0.18), inset 0 0 12px rgba(${accentRgb}, 0.04)`,
      }}
    >
      <span className="flex items-center justify-center gap-2">
        Enter Workshop
        <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
      </span>
    </button>
  );
}

export function WorkshopPanel({
  workshopId,
}: {
  workshopId: WorkshopPanelData["workshopId"];
}) {
  // Phone-tier pane switch — mirrors MC_00's Operating Model / Program
  // Rollup toggle (MissionControl.tsx). Stacking the diagram at a fixed
  // 38% height squeezed it the same way the ring/rollup split did; each
  // WorkshopPanel instance remounts on workshop change (keyed by
  // `activePanel` in MissionControl's AnimatePresence), so this resets
  // to "diagram" automatically per workshop. Desktop (`lg:`) ignores it
  // and always shows both side by side, same as before.
  const [mobilePane, setMobilePane] = useState<"diagram" | "brief">("diagram");
  const data = WORKSHOP_PANEL_DATA[workshopId];
  const override = useEffectiveWorkshopOverride(workshopId);
  const resolved = useMemo(
    () => resolveWorkshopDiagram(workshopId, override),
    [workshopId, override],
  );
  const accentVar = ACCENT_VAR[data.accent];
  const navigate = useNavigate();
  const ariaLabel = `${data.workshopId} — ${resolved.bubbles
    .map((b) => b.titleLines.join(" "))
    .join(", ")}`;

  const svg = (
    <svg
      viewBox={resolved.viewBox}
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      className="block h-full w-full"
      style={{ overflow: "hidden" }}
      role="img"
      aria-label={ariaLabel}
    >
      <PanelDefs id={data.workshopId} accent={data.accent} />

      {resolved.connectors.map(([x1, y1, x2, y2], i) => (
        <line
          key={`con-${i}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={accentVar}
          strokeOpacity={0.18}
          strokeWidth={1}
          strokeDasharray="4,3"
        />
      ))}

      {resolved.bubbles.map((config) => (
        <Bubble key={config.id} config={config} />
      ))}
    </svg>
  );

  // Side-by-side only from lg: below that the 240px activities column would
  // leave the bubble diagram around 100px wide, so mobile shows one pane
  // at a time instead (`mobilePane`, toggled by the tabs below).
  return (
    <div className="flex h-full w-full min-h-0 flex-col gap-2">
      <div
        className="mc-mobile-tabs flex flex-shrink-0 gap-2 lg:hidden"
        role="tablist"
        aria-label={`${data.workshopId} view`}
      >
        <button
          type="button"
          role="tab"
          aria-selected={mobilePane === "diagram"}
          onClick={() => setMobilePane("diagram")}
          className={`mc-mobile-tab ${mobilePane === "diagram" ? "mc-mobile-tab-active" : ""}`}
        >
          Diagram
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobilePane === "brief"}
          onClick={() => setMobilePane("brief")}
          className={`mc-mobile-tab ${mobilePane === "brief" ? "mc-mobile-tab-active" : ""}`}
        >
          Brief
        </button>
      </div>

      <div
        className="bg-card/70 relative grid min-h-0 flex-1 grid-cols-1 grid-rows-1 gap-3 overflow-hidden rounded-md border p-3 lg:grid-cols-[1fr_minmax(240px,300px)]"
        style={{ borderColor: `color-mix(in srgb, ${accentVar} 30%, transparent)` }}
      >
        {/* Bubble diagram column — wraps the SmokeCanvas + wordmark + SVG
            so the activities column on the right stays clear of the smoke
            backdrop. `lg:contents` makes this wrapper transparent to the
            grid at desktop (the diagram box + activities column go back
            to being the grid's only two items, same as before); below
            `lg` it's a flex column so the phone-only CTA can sit under
            the diagram — without it, "Enter Workshop" only existed on
            the Brief tab, forcing a tab switch to leave the diagram. */}
        <div
          className={`flex min-h-0 flex-col gap-2 lg:contents ${
            mobilePane === "brief" ? "hidden lg:contents" : ""
          }`}
        >
          <div
            className="relative min-h-0 flex-1 overflow-hidden rounded-lg border"
            style={{
              borderColor: `color-mix(in srgb, ${accentVar} 20%, transparent)`,
            }}
          >
            <SmokeCanvas
              className="pointer-events-none absolute inset-0 z-0 h-full w-full"
              cssVarName={ACCENT_CSS_VAR[data.accent]}
            />

            {/* The wordmark lines are nowrap at 0.3em tracking, so any column
                narrower than the text spills across the bubbles. Below sm it
                drops and the diagram takes the full width. */}
            {data.wordmark ? (
              <div className="relative z-[1] grid h-full w-full grid-cols-1 grid-rows-1 gap-2 p-4 sm:grid-cols-[minmax(140px,18%)_1fr] sm:p-6">
                <Wordmark
                  wordmark={data.wordmark}
                  accent={data.accent}
                  className="hidden sm:flex"
                />
                {svg}
              </div>
            ) : (
              <div className="relative z-[1] flex h-full w-full items-center justify-center p-4 sm:p-6">
                {svg}
              </div>
            )}
          </div>

          <div className="flex-shrink-0 lg:hidden">
            <EnterWorkshopButton
              accent={data.accent}
              onEnterWorkshop={() => navigate(`/workshop/${data.workshopId}`)}
            />
          </div>
        </div>

        {/* Right column — brief + activities (CTA lives inside it). */}
        <div
          className={`min-h-0 lg:block ${mobilePane === "brief" ? "block" : "hidden"}`}
        >
          <ActivitiesColumn
            data={data}
            accent={data.accent}
            onEnterWorkshop={() => navigate(`/workshop/${data.workshopId}`)}
          />
        </div>
      </div>
    </div>
  );
}
